<template>
  <BaseFormWrapper
    ref="form"
    class="form-wrapper-layout column full-height overflow-hidden"
    :class="layoutClasses"
    form-class="form-wrapper-layout__container"
    @submit="emit('submit', $event)"
  >
    <div
      v-if="slots.header || title"
      :class="{ 'form-wrapper-layout__header': !headerSections }"
    >
      <slot name="header">
        <div class="form-wrapper-layout__title">
          <h2>
            {{ title }}
          </h2>
        </div>
      </slot>
    </div>
    <BaseLoadingBar
      v-if="loading"
      height="2"
    />
    <div
      class="form-wrapper-layout__contents"
      :class="contentClass"
    >
      <slot />
    </div>
    <div
      v-if="slots.footer"
      class="form-wrapper-layout__footer"
    >
      <slot name="footer" />
    </div>
  </BaseFormWrapper>
</template>

<script setup lang="ts">
import { computed, ref, useSlots } from "vue";

import BaseFormWrapper from "shared/components/base/BaseFormWrapper";
import BaseLoadingBar from "shared/components/base/BaseLoadingBar";

export interface Props {
  contentSections?: boolean;
  flatHeader?: boolean;
  fullPage?: boolean;
  headerFooterSize?: "md" | "lg";
  headerSections?: boolean;
  loading?: boolean;
  size?: "md" | "lg";
  title?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  size: "md",
  headerFooterSize: "md",
});

const emit = defineEmits<{
  submit: [Event];
}>();

const slots = useSlots();
const form = ref<InstanceType<typeof BaseFormWrapper>>();

const layoutClasses = computed<string[]>(() => {
  const classes = [
    `form-wrapper-layout__${props.size}`,
    `form-wrapper-layout__header-footer-${props.headerFooterSize}`,
  ];

  if (props.flatHeader) {
    classes.push("form-wrapper-layout__flat-header");
  }

  if (props.fullPage) {
    classes.push("form-wrapper-layout__full-page");
  }

  return classes;
});

const contentClass = computed<string>(() =>
  props.contentSections || props.fullPage
    ? "form-wrapper-layout__sections"
    : "form-wrapper-layout__content"
);

function validate(): Promise<boolean> {
  if (!form.value) {
    return Promise.resolve(false);
  }

  return form.value.isValid();
}

defineExpose({
  validate,
});
</script>

<style lang="scss">
.form-wrapper-layout {
  --c-form-wrapper-layout-size: 524px;
  --c-form-wrapper-layout-header-footer-size: 524px;
  --c-form-wrapper-section-padding-block: 24px;
  --c-form-wrapper-border-color: var(--s-color-denim-2);
  --c-form-wrapper-border-radius: 10px;
  --c-form-wrapper-header-border-color: var(--c-form-wrapper-border-color);
  --c-form-wrapper-header-bg: white;
  --c-form-wrapper-header-min-height: 48px;
  --c-form-wrapper-contents-bg: white;
  --c-form-wrapper-content-border-top-radius: 0;
  --c-form-wrapper-content-border-bottom-radius: 0;

  padding: 16px;
  width: 100%;

  &__container {
    overflow: hidden;
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__lg {
    --c-form-wrapper-layout-size: 700px;
  }

  &__header-footer-lg {
    --c-form-wrapper-layout-header-footer-size: 700px;
  }

  &__header,
  &__footer {
    display: grid;
    grid-template-columns: minmax(
      auto,
      var(--c-form-wrapper-layout-header-footer-size)
    );
    justify-content: center;
  }

  &__header {
    border: 1px solid var(--c-form-wrapper-header-border-color);
    background-color: var(--c-form-wrapper-header-bg);
    min-height: var(--c-form-wrapper-header-min-height);
    padding: 0 16px;
    margin-bottom: -1px;

    h2 {
      line-height: 1;
      margin: 0;
      font-size: 16px;
    }

    &:first-child {
      border-top-left-radius: var(--c-form-wrapper-border-radius);
      border-top-right-radius: var(--c-form-wrapper-border-radius);
    }
  }

  &__title {
    display: flex;
    align-items: center;
    height: 100%;
  }

  &__footer {
    padding: 8px 16px;
    border: 1px solid var(--c-form-wrapper-border-color);
    background: white;
    border-bottom-left-radius: var(--c-form-wrapper-border-radius);
    border-bottom-right-radius: var(--c-form-wrapper-border-radius);
    margin-top: -1px;

    &:empty {
      display: none;
    }
  }

  &__contents {
    background-color: var(--c-form-wrapper-contents-bg);
    border: 1px solid var(--c-form-wrapper-border-color);
    border-radius: var(--c-form-wrapper-content-border-top-radius)
      var(--c-form-wrapper-content-border-top-radius)
      var(--c-form-wrapper-content-border-bottom-radius)
      var(--c-form-wrapper-content-border-bottom-radius);
    flex: 1;
    overflow: auto;
    position: relative;
  }

  // Doubled class keeps specificity at (0,2,0) so `display: grid` wins over
  // Quasar's `.row, .column, .flex { display: flex }` (0,1,0) no matter which
  // order the extracted stylesheets land in the production bundle.
  &__section.form-wrapper-layout__section,
  &__content.form-wrapper-layout__content {
    > * {
      display: grid;
      grid-template-columns: minmax(auto, var(--c-form-wrapper-layout-size));
      padding-block: var(--c-form-wrapper-section-padding-block);
      padding-inline: 16px;
      border-bottom: 1px solid var(--c-form-wrapper-border-color);
      justify-content: center;
    }
  }

  &__content.form-wrapper-layout__content {
    > * {
      &:last-child {
        border-bottom: none;
      }
    }
  }

  &__section {
    // Doubled to match the raised specificity of the `> *` rule above, so this
    // modifier keeps overriding its `border-bottom` as it always has.
    &-no-border.form-wrapper-layout__section-no-border,
    &-no-border.form-wrapper-layout__section-no-border > * {
      border-bottom: none;
    }

    &-condensed {
      --c-form-wrapper-section-padding-block: 12px;
    }
  }

  &__flat-header {
    --c-form-wrapper-header-bg: transparent;
    --c-form-wrapper-header-border-color: transparent;
    --c-form-wrapper-header-min-height: 0;
    --c-form-wrapper-content-border-top-radius: var(
      --c-form-wrapper-border-radius
    );
  }

  &__full-page {
    --c-form-wrapper-layout-size: 100%;
    --c-form-wrapper-layout-header-footer-size: 100%;
    --c-form-wrapper-contents-bg: var(--s-color-denim-1);

    // WCAG 1.4.10 Reflow: the header and footer are sized by their content, so
    // on a short viewport (400% zoom of 1280x1024 leaves 256px) they squeeze
    // the scrolling contents pane down to a few dozen pixels. Below that height
    // the layout stops being its own scroll region and lets the page scroll the
    // header, contents and footer as one.
    @media (height <= 400px) {
      height: auto;
      overflow: visible !important; // beats Quasar's .overflow-hidden utility

      .form-wrapper-layout__container,
      .form-wrapper-layout__contents {
        overflow: visible;
      }
    }
  }

  &:not(:has(.form-wrapper-layout__footer)),
  &:has(.form-wrapper-layout__footer:empty) {
    --c-form-wrapper-content-border-bottom-radius: var(
      --c-form-wrapper-border-radius
    );
  }
}

.mobile {
  .form-wrapper-layout {
    padding: 0 0 calc(env(safe-area-inset-bottom));

    &__container {
      border: none;
      border-radius: 0;
    }
  }
}
</style>
